import React, { useState, useRef, useCallback, useEffect } from 'react';
import './AdminDataSync.css';
import LogsPopup from '../../components/LogsPopup/LogsPopup';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Desired column order (email shows the date range inline, approved last)
const COLUMN_ORDER = ['email', 'company', 'leave_type', 'created_at', 'day_count', 'remark', 'approved'];
const getColumnRank = (key) => {
  const idx = COLUMN_ORDER.indexOf(key);
  return idx === -1 ? COLUMN_ORDER.length - 1 : idx;
};

// Icon + color class for each leave type
const getLeaveTypeMeta = (type) => {
  const normalized = String(type || '').toLowerCase();
  if (normalized.includes('sick')) return { icon: '🤒', className: 'leave-sick' };
  if (normalized.includes('earned') || normalized.includes('vacation')) return { icon: '🏖️', className: 'leave-earned' };
  if (normalized.includes('maternity') || normalized.includes('paternity')) return { icon: '👶', className: 'leave-maternity' };
  if (normalized.includes('casual')) return { icon: '🙂', className: 'leave-casual' };
  if (normalized.includes('wfh')) return { icon: '🏠', className: 'leave-wfh' };
  if (normalized.includes('birthday')) return { icon: '🎂', className: 'leave-birthday' };
  if (normalized.includes('unpaid')) return { icon: '💸', className: 'leave-unpaid' };
  return { icon: '📌', className: 'leave-other' };
};

// Formats a date as e.g. "20th Dec, 2026". Handles ISO strings as well as
// DD/MM/YY(YY) strings (master_data stores dates in that format).
const formatDate = (value) => {
  if (!value) return value;

  let date;
  const ddmmyyMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(value);
  if (ddmmyyMatch) {
    const [, d, m, y] = ddmmyyMatch;
    const year = y.length === 2 ? 2000 + parseInt(y, 10) : parseInt(y, 10);
    date = new Date(year, parseInt(m, 10) - 1, parseInt(d, 10));
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return value;

  const day = date.getDate();
  const suffix = (day % 10 === 1 && day !== 11) ? 'st'
    : (day % 10 === 2 && day !== 12) ? 'nd'
    : (day % 10 === 3 && day !== 13) ? 'rd'
    : 'th';

  return `${day}${suffix} ${MONTH_NAMES[date.getMonth()]}, ${date.getFullYear()}`;
};

const AdminDataSync = () => {
  const [masterData, setMasterData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [importCompany, setImportCompany] = useState('Aays');
  const [showModal, setShowModal] = useState(false);
  const [showLogsPopup, setShowLogsPopup] = useState(false);
  const [logs, setLogs] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [approvedFilter, setApprovedFilter] = useState('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('');
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [yearOptions, setYearOptions] = useState([]);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, limit: 50 });
  const fileInputRef = useRef(null);
  const logsRef = useRef([]);

  // Fetch master_data whenever page or filters change
  useEffect(() => {
    fetchMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, approvedFilter, leaveTypeFilter, companyFilter, yearFilter]);

  // Fetch distinct leave types once, for the filter dropdown
  useEffect(() => {
    const fetchLeaveTypes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/admin/master-data/leave-types`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('aays_token')}` },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setLeaveTypeOptions(data.data || []);
        }
      } catch (err) {
        console.error('Error fetching leave types:', err);
      }
    };
    fetchLeaveTypes();
  }, []);

  // Fetch distinct companies once, for the filter dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/admin/master-data/companies`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('aays_token')}` },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setCompanyOptions(data.data || []);
        }
      } catch (err) {
        console.error('Error fetching companies:', err);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch distinct years once, for the filter dropdown
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/admin/master-data/years`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('aays_token')}` },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setYearOptions(data.data || []);
        }
      } catch (err) {
        console.error('Error fetching years:', err);
      }
    };
    fetchYears();
  }, []);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: pagination.limit });
      if (searchTerm) params.set('search', searchTerm);
      if (approvedFilter) params.set('approved', approvedFilter);
      if (leaveTypeFilter) params.set('leave_type', leaveTypeFilter);
      if (companyFilter) params.set('company', companyFilter);
      if (yearFilter) params.set('year', yearFilter);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/master-data?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('aays_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch master data: ${response.status}`);
      }

      const data = await response.json();
      setMasterData(data.data || []);
      setPagination(data.pagination || { total: 0, totalPages: 1, limit: 50 });
    } catch (err) {
      console.error('Error fetching master data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleApprovedFilterChange = (e) => {
    setApprovedFilter(e.target.value);
    setPage(1);
  };

  const handleLeaveTypeFilterChange = (e) => {
    setLeaveTypeFilter(e.target.value);
    setPage(1);
  };

  const handleCompanyFilterChange = (e) => {
    setCompanyFilter(e.target.value);
    setPage(1);
  };

  const handleYearFilterChange = (e) => {
    setYearFilter(e.target.value);
    setPage(1);
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortColumn === column && sortDirection === 'asc') {
      direction = 'desc';
    }

    const sorted = [...masterData].sort((a, b) => {
      const aVal = a[column] || '';
      const bVal = b[column] || '';

      if (direction === 'asc') {
        return String(aVal).localeCompare(String(bVal));
      } else {
        return String(bVal).localeCompare(String(aVal));
      }
    });

    setMasterData(sorted);
    setSortColumn(column);
    setSortDirection(direction);
  };

  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    logsRef.current = [...logsRef.current, { message: logEntry, type }];
    setLogs([...logsRef.current]);
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const handleUpload = async () => {
    if (!csvFile) {
      alert('Please select a CSV file');
      return;
    }

    setIsUploading(true);
    setShowLogsPopup(true);
    logsRef.current = [];
    setLogs([]);
    addLog('Starting CSV import...', 'info');

    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('company', importCompany);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/upload-csv`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('aays_token')}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.trim()) {
            try {
              const data = JSON.parse(line);
              if (data.log) {
                addLog(data.log, data.type || 'info');
              }
            } catch (e) {
              // Non-JSON lines are ignored
            }
          }
        }
      }

      addLog('✅ CSV import completed!', 'success');
      addLog('Starting automatic leave balance sync...', 'info');

      // Automatic sync after upload
      await performSync();

      setCsvFile(null);
      setShowModal(false);
      setPage(1);
      await fetchMasterData();
    } catch (error) {
      addLog('❌ Upload failed: ' + error.message, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const performSync = async () => {
    return new Promise(async (resolve) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/admin/sync-leave-balance`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('aays_token')}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Sync failed with status ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.trim()) {
              try {
                const data = JSON.parse(line);
                if (data.log) {
                  addLog(data.log, data.type || 'info');
                }
              } catch (e) {
                // Non-JSON lines are ignored
              }
            }
          }
        }

        addLog('✅ Leave balance sync completed!', 'success');
        resolve();
      } catch (error) {
        addLog('❌ Sync failed: ' + error.message, 'error');
        resolve();
      }
    });
  };

  const handleManualSync = async () => {
    setShowLogsPopup(true);
    logsRef.current = [];
    setLogs([]);
    addLog('Starting manual leave balance sync...', 'info');
    await performSync();
  };

  // Creates/cancels leave_requests rows to match master_data (used by the
  // Leave Management page), the same way the "Leave Details" view and
  // syncLeaveBalance already treat master_data as the source of truth.
  // Always previews (dryRun) first and asks for confirmation before writing.
  const handleBackfillLeaveRequests = async () => {
    setShowLogsPopup(true);
    logsRef.current = [];
    setLogs([]);
    addLog('Checking for missing/mismatched leave requests (preview only)...', 'info');

    const authHeader = 'Bearer ' + localStorage.getItem('aays_token');

    try {
      const preview = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/backfill-leave-requests?dryRun=true`,
        { method: 'POST', headers: { Authorization: authHeader } }
      ).then((r) => r.json());

      if (!preview.success) throw new Error(preview.message || 'Preview failed');

      const { created, cancelled, phantomsCancelled, skippedExisting, skippedUnmapped } = preview.summary;
      addLog(`Would create ${created} request(s), cancel ${cancelled} matched-cancel request(s).`, 'info');
      addLog(`Would cancel ${phantomsCancelled} off-by-one-day duplicate(s).`, 'info');
      addLog(`Already up to date: ${skippedExisting}. Unmapped leave types skipped: ${skippedUnmapped}.`, 'info');

      if (created === 0 && cancelled === 0 && phantomsCancelled === 0) {
        addLog('✅ Leave Management already matches master data. Nothing to do.', 'success');
        return;
      }

      const confirmed = window.confirm(
        `This will create ${created} request(s), cancel ${cancelled} matched-cancel request(s), and cancel ${phantomsCancelled} duplicate(s). Continue?`
      );
      if (!confirmed) {
        addLog('Cancelled by user. No changes made.', 'warning');
        return;
      }

      addLog('Applying changes...', 'info');
      const applied = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/backfill-leave-requests?dryRun=false`,
        { method: 'POST', headers: { Authorization: authHeader } }
      ).then((r) => r.json());

      if (!applied.success) throw new Error(applied.message || 'Backfill failed');

      addLog(
        `✅ Created ${applied.summary.created}, cancelled ${applied.summary.cancelled}, removed duplicates ${applied.summary.phantomsCancelled}.`,
        'success'
      );
    } catch (error) {
      addLog('❌ Backfill failed: ' + error.message, 'error');
    }
  };

  return (
    <div>
      <div className="sync-container">
        {/* Header */}
        <div className="page-header">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: '0.0rem' }} className="header-left">
            <h1>Data Sync Management</h1>
            <p style={{marginTop:"0px"}} className="subtitle">Manage master data and synchronize leave balances</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              📥 Import Data from CSV
            </button>
            <button className="btn btn-info" onClick={handleManualSync}>
              🔄 Sync Leave Balance
            </button>
            <button className="btn btn-secondary" onClick={handleBackfillLeaveRequests}>
              📋 Sync Missing Requests
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <input
            type="text"
            placeholder="Search master data..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
          <select
            value={leaveTypeFilter}
            onChange={handleLeaveTypeFilterChange}
            className="filter-select"
          >
            <option value="">All Leave Types</option>
            {leaveTypeOptions.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <select
            value={companyFilter}
            onChange={handleCompanyFilterChange}
            className="filter-select"
          >
            <option value="">All Companies</option>
            {companyOptions.map((company) => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
          <select
            value={yearFilter}
            onChange={handleYearFilterChange}
            className="filter-select"
          >
            <option value="">All Years</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select
            value={approvedFilter}
            onChange={handleApprovedFilterChange}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="true">Approved</option>
            <option value="false">Pending</option>
          </select>
          <span className="record-count">
            {pagination.total} records
          </span>
        </div>

        {/* Master Data Table */}
        <div className="data-table-card">
          <div className="table-header">
            <h2>Master Data</h2>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading master data...</p>
            </div>
          ) : masterData.length > 0 ? (
            <>
              <div className="table-wrapper">
                <table className="master-data-table">
                  <thead>
                    <tr>
                      {Object.keys(masterData[0])
                        .filter((key) => key !== 'id' && key !== 'user_id' && key !== 'end_date' && key !== 'start_date')
                        .sort((a, b) => getColumnRank(a) - getColumnRank(b))
                        .map((key) => (
                        <th
                          key={key}
                          onClick={() => handleSort(key)}
                          className={`sortable-header${key === 'remark' ? ' remark-col' : ''}`}
                        >
                          {key === 'day_count' ? 'days' : key}
                          {sortColumn === key && (
                            <span className="sort-icon">
                              {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {masterData.map((row, idx) => (
                      <tr key={idx}>
                        {Object.entries(row)
                          .filter(([key]) => key !== 'id' && key !== 'user_id' && key !== 'end_date' && key !== 'start_date')
                          .sort(([a], [b]) => getColumnRank(a) - getColumnRank(b))
                          .map(([key, value], vIdx) => (
                          <td key={vIdx} className={key === 'remark' ? 'remark-col' : ''}>
                            {key === 'email'
                              ? (
                                <>
                                  <span className="email-text">{value}</span>
                                  <br />
                                  <span className="date-range-text">
                                    {formatDate(row.start_date)} to {formatDate(row.end_date)}
                                  </span>
                                </>
                              )
                              : key === 'approved'
                              ? (
                                <span className={`status-badge ${value === true || value === 'true' ? 'status-approved' : 'status-pending'}`}>
                                  {value === true || value === 'true' ? '✅ Approved' : '⏳ Pending'}
                                </span>
                              )
                              : key === 'leave_type'
                              ? (
                                <span className={`leave-type-badge ${getLeaveTypeMeta(value).className}`}>
                                  {getLeaveTypeMeta(value).icon} {value}
                                </span>
                              )
                              : key === 'created_at'
                              ? formatDate(value)
                              : key === 'day_count'
                              ? (
                                <span className="day-count-badge">
                                  🗓️ {value === null || value === undefined ? '—' : value}
                                </span>
                              )
                              : value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    ‹ Prev
                  </button>
                  <span className="pagination-info">
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next ›
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <p>No master data available. Import a CSV file to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => !isUploading && setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📥 Import Data from CSV</h2>
              <button
                className="btn-close"
                onClick={() => !isUploading && setShowModal(false)}
                disabled={isUploading}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="company-select-group">
                <label className="company-option">
                  <input
                    type="radio"
                    name="importCompany"
                    value="Aays"
                    checked={importCompany === 'Aays'}
                    onChange={(e) => setImportCompany(e.target.value)}
                    disabled={isUploading}
                  />
                  Aays Data
                </label>
                <label className="company-option">
                  <input
                    type="radio"
                    name="importCompany"
                    value="Aditect"
                    checked={importCompany === 'Aditect'}
                    onChange={(e) => setImportCompany(e.target.value)}
                    disabled={isUploading}
                  />
                  Aditect Data
                </label>
              </div>

              <div className="upload-area">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".csv"
                  className="file-input"
                  disabled={isUploading}
                />
                <button
                  className="btn btn-upload"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  📂 Choose CSV File
                </button>
                {csvFile && (
                  <div className="file-selected">
                    <span className="file-icon">📄</span>
                    <span className="file-name">{csvFile.name}</span>
                  </div>
                )}
              </div>

              {/* Live Logs */}
              <div className="modal-logs">
                <div className="logs-header">
                  <h3>📋 Import Logs</h3>
                </div>
                <div className="logs-content">
                  {logs.length > 0 ? (
                    logs.map((log, idx) => (
                      <div
                        key={idx}
                        className={`log-entry log-${log.type}`}
                      >
                        {log.message}
                      </div>
                    ))
                  ) : (
                    <div className="empty-logs">
                      Logs will appear here during import...
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-cancel"
                onClick={() => setShowModal(false)}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleUpload}
                disabled={!csvFile || isUploading}
              >
                {isUploading ? '⏳ Uploading...' : '✓ Upload & Sync'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Popup */}
      <LogsPopup
        isOpen={showLogsPopup}
        onClose={() => setShowLogsPopup(false)}
        logs={logs}
        isLoading={isUploading}
      />
    </div>
  );
};


export default AdminDataSync;
